import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Garde-fou global : capture les erreurs JS non rattrapées dans le sous-arbre
 * React et affiche un fallback plutôt qu'un écran blanc.
 *
 * Réfs : audit V2.4 frontend §10 — un crash dans un composant enfant
 * cause sinon un écran blanc sans feedback.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // En prod, on enverra à Sentry. En dev, on logge en console.
    if (import.meta.env.DEV) {
      console.error("[ErrorBoundary]", error, info.componentStack);
    }
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
          <div className="max-w-md w-full rounded-lg border border-red-200 bg-white p-6 shadow-sm">
            <h1 className="text-lg font-semibold text-gray-900">
              Une erreur est survenue
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              L'application a rencontré un problème inattendu. Vous pouvez
              réessayer ou rafraîchir la page.
            </p>
            {import.meta.env.DEV && this.state.error && (
              <pre className="mt-3 max-h-40 overflow-auto rounded bg-gray-100 p-2 text-xs text-red-800">
                {this.state.error.message}
              </pre>
            )}
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={this.handleReset}
                className="rounded bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700"
              >
                Réessayer
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Rafraîchir
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
